import {
  CompassCardComponent,
  CompassCardComponentProps,
} from 'react-scale/corp/views/DataCompassV2/types';
import { useCompassCard } from '@scale/compass-ui/store';
import { CdsUploadRequiredColumns } from '@scale/genai-compass-shared/cards/cds_upload';
import { ColumnSubType } from '@scale/compass-shared/types';
import { getTaskIdColumn, getGoogleDriveUrlColumn, getImageUrlColumn } from './utils';
import { CdsUploadInstructions } from './CdsUploadInstructions';
import { useEffect, useMemo } from 'react';
import { AutoIdentifyColumn } from '../../components/AutoIdentifyColumn';

// Define constants for column detection
const TASK_ID_SUBTYPE = ColumnSubType.TASK_ID;
const GDRIVE_URL_HEADER = CdsUploadRequiredColumns.GOOGLE_DRIVE_URL;
const IMAGE_URL_HEADER = CdsUploadRequiredColumns.IMAGE_URL;

const CdsUploadCardComponent: CompassCardComponent = ({ index }: CompassCardComponentProps) => {
  const { updateCardCanSubmit } = useCompassCard(index);
  const { retrievedOutput } = useCompassCard(index - 1);

  // Get the schema columns
  const columns = retrievedOutput?.response?.schema || [];

  // Use helper functions for backward compatibility
  const taskIdColumn = getTaskIdColumn(retrievedOutput);
  const googleDriveUrlColumn = getGoogleDriveUrlColumn(retrievedOutput);
  const imageUrlColumn = getImageUrlColumn(retrievedOutput);

  // Get columns by checking header names
  const hasTaskIdColumn = !!taskIdColumn;
  const hasGDriveUrlColumn = !!googleDriveUrlColumn;
  const hasImageUrlColumn = !!imageUrlColumn;

  // Check if we have exactly one URL column type
  const hasExactlyOneUrlColumn =
    (hasGDriveUrlColumn && !hasImageUrlColumn) || (!hasGDriveUrlColumn && hasImageUrlColumn);

  useEffect(() => {
    // Update whether the card can be submitted
    updateCardCanSubmit(hasTaskIdColumn && hasExactlyOneUrlColumn);
  }, [taskIdColumn, googleDriveUrlColumn, imageUrlColumn]);

  const errorMessage = useMemo(() => {
    if (!hasTaskIdColumn) {
      return `Required column ${CdsUploadRequiredColumns.TASK} missing`;
    }

    if (!hasGDriveUrlColumn && !hasImageUrlColumn) {
      return `At least one URL column is required: ${GDRIVE_URL_HEADER} or ${IMAGE_URL_HEADER}`;
    }

    if (hasGDriveUrlColumn && hasImageUrlColumn) {
      return `Only one URL column should be present, not both: ${GDRIVE_URL_HEADER} and ${IMAGE_URL_HEADER}`;
    }

    return null;
  }, [hasTaskIdColumn, hasGDriveUrlColumn, hasImageUrlColumn]);

  return (
    <div className="flex flex-col gap-8">
      {errorMessage && <div className="text-red-600">{errorMessage}</div>}

      <div className="flex flex-col gap-2 p-4 bg-gray-50 rounded-md">
        <h3 className="text-sm font-medium text-gray-700">Column Detection</h3>

        <AutoIdentifyColumn columns={columns} columnSubType={TASK_ID_SUBTYPE} />

        <div className="flex items-center gap-2 mt-2">
          {hasGDriveUrlColumn ? (
            <div className="flex items-center gap-2 text-neutral-600">
              <span className="text-primary-600">✓</span>
              <span className="text-sm">
                Using column{' '}
                <code className="px-1 py-0.5 bg-blue-100 rounded text-blue-700">
                  {GDRIVE_URL_HEADER}
                </code>
                &nbsp;for Google Drive URL
              </span>
            </div>
          ) : hasImageUrlColumn ? (
            <div className="flex items-center gap-2 text-neutral-600">
              <span className="text-primary-600">✓</span>
              <span className="text-sm">
                Using column{' '}
                <code className="px-1 py-0.5 bg-blue-100 rounded text-blue-700">
                  {IMAGE_URL_HEADER}
                </code>
                &nbsp;for Image URL
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-gray-400">
              <span className="text-gray-400">ⓘ</span>
              <span className="text-sm">No URL column detected</span>
            </div>
          )}
        </div>
      </div>

      <CdsUploadInstructions />
    </div>
  );
};

CdsUploadCardComponent.displayName = 'CdsUploadCardComponent';

export default CdsUploadCardComponent;
